// src/components/AuthProvider.tsx
'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import { usePathname, useRouter } from 'next/navigation';
import Link from 'next/link';
import { ShieldAlert, LogOut, Info, Server, RefreshCw } from 'lucide-react';
import axios from 'axios';

import { useLanguage } from '@/components/LanguageProvider';
import {
  AppContext,
  AuthStatus,
  IDP_API_URL,
  LOCAL_API_URL,
  RegionData,
  UserType,
  checkGlobalSSO,
  fetchAppContext,
  fetchMe,
  getCachedAppContext,
  invalidateAppContext,
  setCachedAppContext,
} from '@/lib/auth/app-context';

// ---------------------------------------------------------------
// Контракт контекста
// ---------------------------------------------------------------
interface AuthContextType {
  status: AuthStatus;
  user: UserType | null;
  regionData: RegionData | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  openLoginModal: () => void;
  closeLoginModal: () => void;
  logout: () => void;
  register: (selectedNickname?: string) => Promise<void>;
  refreshUser: (force?: boolean) => Promise<boolean>;
}

interface AuthProviderProps {
  children: React.ReactNode;
  initialUser?: UserType | null;
  initialStatus?: AuthStatus;
  initialRegionData?: RegionData | null;
}

const AuthContext = createContext<AuthContextType>({} as any);

const SILENT_CHECK_INTERVAL_MS = 3 * 60 * 1000;
const SSO_CHECK_STORAGE_KEY = 'sso_checked_v1';

// ---------------------------------------------------------------
// Axios + 401 interceptor
// ---------------------------------------------------------------
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

export const api = axios.create({
  withCredentials: true,
});

const refreshAuthToken = async () => {
  try {
    const res = await fetch(`${LOCAL_API_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (res.ok) {
      // cookie обновились → кэш недействителен
      invalidateAppContext();
      return true;
    }
  } catch (e) {
    console.warn('Local refresh failed, falling back to SSO', e);
  }

  const ok = await checkGlobalSSO();
  if (ok) {
    invalidateAppContext();
    return true;
  }
  throw new Error('SSO Auth failed completely');
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest) return Promise.reject(error);

    const pathname: string = (() => {
      try {
        return new URL(originalRequest.url, LOCAL_API_URL).pathname;
      } catch {
        return originalRequest.url ?? '';
      }
    })();

    const ignoredPaths = [
      '/auth/me',
      '/auth/refresh',
      '/auth/logout',
      '/auth/login',
    ];
    const isIgnored = ignoredPaths.some((p) => pathname.endsWith(p));

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isIgnored
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) =>
          failedQueue.push({ resolve, reject })
        )
          .then(() => api(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;
      try {
        await refreshAuthToken();
        isRefreshing = false;
        processQueue(null, 'success');
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------
// PKCE helpers
// ---------------------------------------------------------------
function generateRandomString(length: number) {
  const possible =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  return Array.from({ length }, () =>
    possible.charAt(Math.floor(Math.random() * possible.length))
  ).join('');
}

async function generateCodeChallenge(codeVerifier: string) {
  const encoder = new TextEncoder();
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    encoder.encode(codeVerifier)
  );
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

const LOAD_STYLES = `
.loading-cube-standalone { width: 72px; height: 72px; background: #121214; border: 1px solid rgba(255,255,255,0.05); border-radius: 20px; display: flex; align-items: center; justify-content: center; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5); animation: fadeScaleIn 0.3s ease-out; }
@keyframes fadeScaleIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
.spinner { width: 28px; height: 28px; border: 3px solid rgba(255, 255, 255, 0.1); border-left-color: #3b82f6; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { 100% { transform: rotate(360deg); } }
`;

// ---------------------------------------------------------------
// RegistrationModal (без изменений, только типы)
// ---------------------------------------------------------------
const RegistrationModal = ({
  user,
  regionData,
  onConfirm,
  onCancel,
  isProcessing,
  refreshUser,
}: {
  user: UserType;
  regionData: RegionData | null;
  onConfirm: (selectedNickname?: string) => Promise<void>;
  onCancel: () => void;
  isProcessing: boolean;
  refreshUser: (force?: boolean) => Promise<boolean>;
}) => {
  const { t } = useLanguage();
  const tr = t.auth.reg;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const hasJava = !!user.mcNickname;
  const hasBedrock = !!user.mcBedrock;
  const hasAnyAccount = hasJava || hasBedrock;

  const [agreed, setAgreed] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<'java' | 'bedrock'>(
    hasJava ? 'java' : 'bedrock'
  );
  const [isRefreshingState, setIsRefreshingState] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshingState(true);
    await refreshUser(true); // ← force, потому что пользователь сам нажал
    setIsRefreshingState(false);
  };

  const cisCountries = [
    'RU', 'KZ', 'GE', 'AZ', 'KG', 'TM', 'TJ', 'MN', 'UZ', 'BY', 'MD', 'IL', 'UA',
  ];

  const serverIp =
    regionData?.countryCode &&
    cisCountries.includes(regionData.countryCode.toUpperCase())
      ? 'rp.royolands.com'
      : 'play.royolands.com';

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-4">
      <div className="bg-[#18181b] border border-white/10 rounded-[24px] w-full max-w-md p-6 shadow-xl relative">
        {!hasAnyAccount ? (
          <>
            <h2 className="text-xl font-bold text-white mb-2">
              {tr.bindTitle}
            </h2>
            <p className="text-zinc-400 text-sm mb-4">{tr.bindDesc}</p>

            <div className="bg-indigo-500/10 border border-indigo-500/20 p-4 rounded-xl mb-6">
              <div className="flex items-center gap-3 mb-3">
                <Server className="text-indigo-400 w-5 h-5" />
                <span className="text-white font-medium">{tr.bindHowTo}</span>
              </div>
              <ol className="text-sm text-zinc-300 space-y-2 ml-4 list-decimal">
                <li>{tr.bindStep1}</li>
                <li>
                  {tr.bindStep2}{' '}
                  <b className="text-white select-all">{serverIp}</b>
                </li>
                <li>{tr.bindStep3}</li>
                <li>{tr.bindStep4}</li>
              </ol>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onCancel}
                className="flex-1 py-3 rounded-xl bg-[#27272a] hover:bg-[#3f3f46] text-white font-medium transition"
              >
                {tr.btnCancel}
              </button>
              <button
                onClick={handleRefresh}
                disabled={isRefreshingState}
                className="flex-[2] py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition flex items-center justify-center gap-2"
              >
                <RefreshCw
                  size={18}
                  className={isRefreshingState ? 'animate-spin' : ''}
                />
                {isRefreshingState ? tr.btnChecking : tr.btnRefresh}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-xl font-bold text-white mb-2">
              {tr.finishTitle}
            </h2>
            <p className="text-zinc-400 text-sm mb-4">{tr.finishDesc}</p>

            <div className="space-y-3 mb-6">
              {hasJava && (
                <div
                  onClick={() => setSelectedPlatform('java')}
                  className={`p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedPlatform === 'java'
                      ? 'bg-indigo-500/10 border-indigo-500'
                      : 'bg-[#1e1e1e] border-white/5 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">
                        {tr.javaTitle}
                      </span>
                      <div className="text-lg font-mono text-white mt-0.5">
                        {user.mcNickname}
                      </div>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedPlatform === 'java'
                          ? 'border-indigo-500'
                          : 'border-zinc-600'
                      }`}
                    >
                      {selectedPlatform === 'java' && (
                        <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full" />
                      )}
                    </div>
                  </div>
                </div>
              )}
              {hasBedrock && (
                <div
                  onClick={() => setSelectedPlatform('bedrock')}
                  className={`p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedPlatform === 'bedrock'
                      ? 'bg-indigo-500/10 border-indigo-500'
                      : 'bg-[#1e1e1e] border-white/5 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">
                        {tr.bedrockTitle}
                      </span>
                      <div className="text-lg font-mono text-white mt-0.5">
                        {user.mcBedrock}
                      </div>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedPlatform === 'bedrock'
                          ? 'border-indigo-500'
                          : 'border-zinc-600'
                      }`}
                    >
                      {selectedPlatform === 'bedrock' && (
                        <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full" />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <label className="flex items-center gap-3 mb-6 cursor-pointer group">
              <div
                className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                  agreed
                    ? 'bg-indigo-500 border-indigo-500'
                    : 'border-zinc-600 group-hover:border-zinc-400'
                }`}
              >
                {agreed && (
                  <svg
                    viewBox="0 0 14 14"
                    fill="none"
                    className="w-3 h-3 text-white"
                  >
                    <path
                      d="M3 7.5L5.5 10L11 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <input
                type="checkbox"
                className="hidden"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <span className="text-sm text-zinc-400 group-hover:text-zinc-300 transition-colors">
                {tr.agreeTerms}
              </span>
            </label>
            <div className="flex gap-3">
              <button
                onClick={onCancel}
                className="flex-1 py-3 rounded-xl bg-[#27272a] hover:bg-[#3f3f46] text-white font-medium transition"
              >
                {tr.btnCancel}
              </button>
              <button
                onClick={() =>
                  onConfirm(
                    selectedPlatform === 'java'
                      ? user.mcNickname
                      : user.mcBedrock
                  )
                }
                disabled={!agreed || isProcessing}
                className={`flex-[2] py-3 rounded-xl text-white font-medium transition ${
                  agreed
                    ? 'bg-indigo-600 hover:bg-indigo-500'
                    : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                }`}
              >
                {isProcessing ? tr.btnCreating : tr.btnCreate}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------
// Provider
// ---------------------------------------------------------------
export const AuthProvider = ({
  children,
  initialUser = null,
  initialStatus = 'GUEST',
  initialRegionData = null,
}: AuthProviderProps) => {
  const { t, locale } = useLanguage();
  const tb = t.auth.ban;

  // 1. Инициализируем ctx из SSR — и сразу кладём в module cache
  const [ctx, setCtx] = useState<AppContext>(() => {
    const seed: AppContext = {
      user: initialUser,
      status: initialStatus,
      regionData: initialRegionData,
      lang: locale,
      fetchedAt: Date.now(),
    };
    setCachedAppContext(seed);
    return seed;
  });

  const { user, status, regionData } = ctx;

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [isIframeLoading, setIsIframeLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  const pkceVerifierRef = useRef<string>('');
  const pathname = usePathname();
  const router = useRouter();

  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  // 2. Подхватываем новые SSR-пропсы после router.refresh()
  useEffect(() => {
    const next: AppContext = {
      user: initialUser,
      status: initialStatus,
      regionData: initialRegionData ?? ctx.regionData,
      lang: locale,
      fetchedAt: Date.now(),
    };
    setCachedAppContext(next);
    setCtx(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUser, initialStatus, initialRegionData]);

  // 3. Если SSR не дал регион — догружаем один раз (module cache защищает от спама)
  useEffect(() => {
    if (ctx.regionData) return;
    let cancelled = false;
    (async () => {
      const region = await (await import('@/lib/auth/app-context')).fetchRegion();
      if (cancelled || !region) return;
      setCtx((c) => ({ ...c, regionData: region }));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 4. Модалка — body overflow
  useEffect(() => {
    if (showLoginModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showLoginModal]);

  // 5. Обновление user (используется в refreshUser, register, silent)
  const applyMeResult = useCallback((me: any): boolean => {
    if (me && (me.status === 'REGISTERED' || me.status === 'NEEDS_REGISTRATION')) {
      setCtx((prev) => {
        const next: AppContext = {
          ...prev,
          user: me.user,
          status: me.status as AuthStatus,
          fetchedAt: Date.now(),
        };
        setCachedAppContext(next);
        return next;
      });
      return true;
    }
    return false;
  }, []);

  const setGuest = useCallback(() => {
    invalidateAppContext();
    setCtx((prev) => {
      const next: AppContext = {
        ...prev,
        user: null,
        status: 'GUEST',
        fetchedAt: Date.now(),
      };
      setCachedAppContext(next);
      return next;
    });
  }, []);

  // 6. Однократный SSO-probe на вкладку, если SSR сказал GUEST
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (ctx.status !== 'GUEST') return;
    if (sessionStorage.getItem(SSO_CHECK_STORAGE_KEY)) return;

    sessionStorage.setItem(SSO_CHECK_STORAGE_KEY, '1');
    let cancelled = false;
    (async () => {
      const ok = await checkGlobalSSO();
      if (cancelled) return;
      if (ok) {
        router.refresh();
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.status]);

  // 7. Silent token check каждые 3 минуты + на visibilitychange
  useEffect(() => {
    const silentTokenCheck = async () => {
      if (
        statusRef.current !== 'REGISTERED' &&
        statusRef.current !== 'NEEDS_REGISTRATION'
      ) {
        return;
      }

      const me = await fetchMe(true);
      if (applyMeResult(me)) return;

      const recovered = await checkGlobalSSO();
      if (recovered) {
        router.refresh();
        return;
      }

      setGuest();
    };

    const interval = setInterval(silentTokenCheck, SILENT_CHECK_INTERVAL_MS);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') silentTokenCheck();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [applyMeResult, setGuest, router]);

  // 8. openLoginModal (PKCE)
  const openLoginModal = useCallback(async () => {
    setIsIframeLoading(true);
    setShowLoginModal(true);

    const verifier = generateRandomString(64);
    pkceVerifierRef.current = verifier;
    const challenge = await generateCodeChallenge(verifier);

    const url =
      `https://account.royolands.com/login-platform/?popup=true&transparent=1` +
      `&client_id=russia&theme=dark` +
      `&redirect_uri=${encodeURIComponent('https://rplands.royolands.com')}` +
      `&code_challenge=${challenge}&code_challenge_method=S256#login`;
    setIframeUrl(url);
  }, []);

  const closeLoginModal = useCallback(() => {
    setShowLoginModal(false);
    setIframeUrl('');
    setIsIframeLoading(false);
  }, []);

  // 9. postMessage от iframe (со строгой проверкой origin)
  useEffect(() => {
    if (!showLoginModal) return;

    const ALLOWED_ORIGIN = 'https://account.royolands.com';

    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== ALLOWED_ORIGIN) return;
      if (!event.data || typeof event.data !== 'object') return;

      const {
        type,
        auth_code,
        code,
        code_verifier: ssoVerifier,
      } = event.data as any;

      const verifierToSend = ssoVerifier || pkceVerifierRef.current;
      const codeToSend = auth_code || code;

      if (type === 'close_popup') {
        closeLoginModal();
        return;
      }

      const isSuccessType =
        type === 'login_success' ||
        type === 'social_auth_success' ||
        (auth_code && type !== 'webpackOk');

      if (!isSuccessType) return;

      setIsIframeLoading(false);

      if (!codeToSend || !verifierToSend) {
        if (type === 'login_success' || type === 'social_auth_success') {
          invalidateAppContext();
          const me = await fetchMe(true);
          applyMeResult(me);
          router.refresh();
          closeLoginModal();
          return;
        }
        closeLoginModal();
        return;
      }

      try {
        const res = await fetch(`${LOCAL_API_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: codeToSend,
            code_verifier: verifierToSend,
          }),
          credentials: 'include',
        });
        if (res.ok) {
          invalidateAppContext();
          const me = await fetchMe(true);
          applyMeResult(me);
          router.refresh();
        }
      } finally {
        closeLoginModal();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [showLoginModal, closeLoginModal, applyMeResult, router]);

  // 10. register
  const register = useCallback(
    async (selectedNickname?: string) => {
      if (!user) return;
      setIsRegistering(true);
      try {
        const res = await fetch(`${LOCAL_API_URL}/user`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            oauthId: user.oauthId,
            nickname: selectedNickname || user.nickname,
            email: user.email,
          }),
          credentials: 'include',
        });
        if (res.ok) {
          invalidateAppContext();
          const me = await fetchMe(true);
          applyMeResult(me);
        }
      } finally {
        setIsRegistering(false);
      }
    },
    [user, applyMeResult]
  );

  // 11. logout
  const logout = useCallback(async () => {
    try {
      await fetch(`${LOCAL_API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {}
    setGuest();
    sessionStorage.removeItem(SSO_CHECK_STORAGE_KEY);
    router.refresh();
  }, [setGuest, router]);

  // 12. refreshUser (с глобальным локом и force)
  const refreshUser = useCallback(
    async (force = false): Promise<boolean> => {
      // module-level lock, а не useRef — переживает ремаунты
      if (!force && getCachedAppContext()) {
        const me = await fetchMe(false);
        if (applyMeResult(me)) return true;
      }

      const fresh = await fetchAppContext(locale, force);
      setCtx(fresh);
      if (fresh.status === 'REGISTERED' || fresh.status === 'NEEDS_REGISTRATION') {
        return true;
      }

      const sso = await checkGlobalSSO();
      if (sso) {
        invalidateAppContext();
        const me = await fetchMe(true);
        if (applyMeResult(me)) return true;
        router.refresh();
        return true;
      }

      setGuest();
      return false;
    },
    [locale, applyMeResult, setGuest, router]
  );

  const isAuthenticated = status === 'REGISTERED';
  const isLoading = false; // SSR всегда знает ответ — LOADING не нужен

  const now = new Date();
  const hasStaffRights =
    user?.role === 'ADMIN' || user?.role === 'MODERATOR' || user?.hasAccess === true;
  const isBanned =
    user &&
    !hasStaffRights &&
    (user.isBanned || (user.bannedUntil && new Date(user.bannedUntil) > now));
  const isAccountStandingPage =
    pathname === '/account-standing' ||
    pathname === '/shop' ||
    pathname === '/support';

  return (
    <AuthContext.Provider
      value={{
        status,
        user,
        regionData,
        isAuthenticated,
        isLoading,
        logout,
        register,
        refreshUser,
        openLoginModal,
        closeLoginModal,
      }}
    >
      <style>{LOAD_STYLES}</style>

      {isBanned && !isAccountStandingPage ? (
        <div className="fixed inset-0 z-[9999999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('/landing2.0/kizrum.png')" }}
          />
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-[#18181b] border border-white/10 rounded-[24px] p-8 max-w-[420px] w-full text-center shadow-2xl">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-5">
              <ShieldAlert className="text-red-500 w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{tb.title}</h2>
            <p className="text-zinc-400 mb-6 text-sm">{tb.desc}</p>
            <div className="bg-[#121212] border border-white/5 rounded-xl p-4 text-left mb-6">
              <div className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider mb-1">
                {tb.reasonTitle}
              </div>
              <div className="text-sm text-zinc-200 leading-relaxed mb-3">
                {user?.banReason || tb.noReason}
              </div>
              {user?.bannedUntil && (
                <>
                  <div className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider mb-1">
                    {tb.untilTitle}
                  </div>
                  <div className="text-sm text-zinc-200">
                    {new Date(user.bannedUntil).toLocaleString(
                      locale === 'ru' ? 'ru-RU' : 'en-US'
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="flex flex-col gap-3">
              <Link
                href="/account-standing"
                className="w-full py-3 bg-white text-black font-semibold rounded-xl hover:bg-zinc-200 transition flex items-center justify-center gap-2"
              >
                <Info size={18} />
                {tb.btnAccount}
              </Link>
              <Link
                href="/support"
                className="w-full py-3 bg-white text-black font-semibold rounded-xl hover:bg-zinc-200 transition flex items-center justify-center gap-2"
              >
                <Info size={18} />
                {tb.btnSupport}
              </Link>
              <Link
                href="/shop"
                className="w-full py-3 bg-white text-black font-semibold rounded-xl hover:bg-zinc-200 transition flex items-center justify-center gap-2"
              >
                <Info size={18} />
                {tb.btnShop}
              </Link>
              <button
                onClick={logout}
                className="w-full py-3 bg-transparent text-zinc-500 hover:text-white font-medium rounded-xl hover:bg-white/5 transition flex items-center justify-center gap-2"
              >
                <LogOut size={18} />
                {tb.btnLogout}
              </button>
            </div>
          </div>
        </div>
      ) : (
        children
      )}

      {showLoginModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" />
          {isIframeLoading && (
            <div className="loading-cube-standalone relative z-[100000]">
              <div className="spinner"></div>
            </div>
          )}
          {iframeUrl && (
            <iframe
              src={iframeUrl}
              onLoad={() => setIsIframeLoading(false)}
              className="absolute inset-0 w-full h-full z-[100000] transition-opacity duration-300"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                colorScheme: 'normal',
                opacity: isIframeLoading ? 0 : 1,
              }}
              allow="autoplay; fullscreen"
            />
          )}
        </div>
      )}

      {status === 'NEEDS_REGISTRATION' && user && !isBanned && (
        <RegistrationModal
          user={user}
          regionData={regionData}
          onConfirm={register}
          onCancel={logout}
          isProcessing={isRegistering}
          refreshUser={refreshUser}
        />
      )}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);